import { BrandMark } from '../components/BrandMark';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { UserMenu } from '../components/common/UserMenu';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import authService from '../services/auth';
import {
  useLLMKeys,
  useSetLLMKey,
  useDeleteLLMKey,
} from '../hooks/useLLMKeys';
import {
  useIntegrations,
  useSetIntegration,
  useRemoveIntegration,
} from '../hooks/useIntegrations';
import { integrationsService } from '../services/integrations';

export default function AccountSettings() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);

  // Profile form
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileMsg, setProfileMsg] = useState<string | null>(null);
  const [profileErr, setProfileErr] = useState<string | null>(null);

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState<string | null>(null);
  const [pwErr, setPwErr] = useState<string | null>(null);

  useEffect(() => {
    setFirstName(user?.firstName ?? '');
    setLastName(user?.lastName ?? '');
  }, [user]);

  async function saveProfile(e: React.FormEvent) {
    e.preventDefault();
    setProfileErr(null);
    setProfileMsg(null);
    setProfileBusy(true);
    try {
      const updated = await authService.updateProfile({ firstName, lastName });
      setUser(updated);
      setProfileMsg('Profile updated.');
    } catch (err: any) {
      setProfileErr(
        err.response?.data?.detail || 'Could not update profile.'
      );
    } finally {
      setProfileBusy(false);
    }
  }

  async function savePassword(e: React.FormEvent) {
    e.preventDefault();
    setPwErr(null);
    setPwMsg(null);
    if (newPassword !== confirmPassword) {
      setPwErr('New passwords do not match.');
      return;
    }
    setPwBusy(true);
    try {
      await authService.changePassword(currentPassword, newPassword);
      setPwMsg('Password changed.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPwErr(
        err.response?.data?.detail ||
          'Could not change password. Check your current password.'
      );
    } finally {
      setPwBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <Link
              to="/dashboard"
              className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
            >
              <BrandMark className="h-7 w-7" />
              <span className="text-xl font-bold">AgentMesh</span>
            </Link>
            <span className="text-sm font-medium text-foreground">Account</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-muted-foreground hidden sm:inline">
              {user?.email}
            </span>
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-2xl space-y-6">
        <div className="mb-2">
          <h2 className="text-3xl font-bold mb-2">Account settings</h2>
          <p className="text-muted-foreground">
            Manage your profile and password.
          </p>
        </div>

        {/* Profile */}
        <Card>
          <CardHeader>
            <CardTitle>Profile</CardTitle>
            <CardDescription>Your name and email.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={saveProfile} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First name</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    maxLength={100}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last name</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    maxLength={100}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" value={user?.email ?? ''} disabled />
                <p className="text-xs text-muted-foreground">
                  Email can't be changed here.
                </p>
              </div>
              {profileMsg && (
                <p className="text-sm text-green-600">{profileMsg}</p>
              )}
              {profileErr && <p className="text-sm text-red-600">{profileErr}</p>}
              <Button type="submit" disabled={profileBusy}>
                {profileBusy ? 'Saving…' : 'Save changes'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Password */}
        <Card>
          <CardHeader>
            <CardTitle>Password</CardTitle>
            <CardDescription>
              Changing your password signs out your other sessions.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={savePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Current password</Label>
                <Input
                  id="currentPassword"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPassword">New password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm new password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </div>
              {pwMsg && <p className="text-sm text-green-600">{pwMsg}</p>}
              {pwErr && <p className="text-sm text-red-600">{pwErr}</p>}
              <Button type="submit" disabled={pwBusy}>
                {pwBusy ? 'Changing…' : 'Change password'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <ProviderKeysCard />
        <IntegrationsCard />
      </main>
    </div>
  );
}

const INTEGRATIONS: {
  kind: string;
  label: string;
  placeholder: string;
  tool: string;
  hint: string;
}[] = [
  {
    kind: 'slack',
    label: 'Slack',
    placeholder: 'https://hooks.slack.com/services/…',
    tool: 'slack_message',
    hint: 'Create an Incoming Webhook in your Slack app and paste its URL.',
  },
  {
    kind: 'discord',
    label: 'Discord',
    placeholder: 'https://discord.com/api/webhooks/…',
    tool: 'discord_message',
    hint: 'Server Settings → Integrations → Webhooks → New Webhook, then copy its URL.',
  },
  {
    kind: 'google_chat',
    label: 'Google Chat',
    placeholder: 'https://chat.googleapis.com/v1/spaces/…',
    tool: 'google_chat_message',
    hint: 'In a Chat space: Apps & integrations → Webhooks → add one, then copy its URL.',
  },
  {
    kind: 'telegram',
    label: 'Telegram',
    placeholder: '123456:AA…|-1001234567890',
    tool: 'telegram_message',
    hint: 'Create a bot with @BotFather, then paste “<botToken>|<chatId>”.',
  },
  {
    kind: 'notion',
    label: 'Notion',
    placeholder: 'secret_… or ntn_…',
    tool: 'notion_create_page',
    hint: 'Create an internal integration in Notion, share your page with it, and paste its token.',
  },
  {
    kind: 'webhook',
    label: 'Outbound webhook',
    placeholder: 'https://your-system.com/hook',
    tool: 'send_webhook',
    hint: 'Any public https endpoint (your API, Zapier, n8n, Make).',
  },
];

function IntegrationsCard() {
  const { data: integrations, refetch } = useIntegrations();
  const setIntegration = useSetIntegration();
  const removeIntegration = useRemoveIntegration();
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [googleMsg, setGoogleMsg] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);

  const byKind = Object.fromEntries(
    (integrations ?? []).map((i) => [i.kind, i])
  );
  const googleConnected = byKind['google_calendar']?.configured;

  // Handle the OAuth callback redirect (?google=connected|error).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const g = params.get('google');
    if (g === 'connected') {
      setGoogleMsg('Google Calendar connected.');
      refetch();
    } else if (g === 'error') {
      setGoogleMsg('Could not connect Google Calendar. Please try again.');
    }
    if (g) window.history.replaceState({}, '', window.location.pathname);
  }, [refetch]);

  async function connectGoogle() {
    setGoogleMsg(null);
    setConnecting(true);
    try {
      const url = await integrationsService.googleAuthUrl();
      window.location.href = url;
    } catch (err: any) {
      setConnecting(false);
      setGoogleMsg(
        err.response?.data?.detail ||
          'Google Calendar is not available on this server.'
      );
    }
  }

  function save(kind: string) {
    setError(null);
    const value = (inputs[kind] || '').trim();
    if (!value) return;
    setIntegration.mutate(
      { kind, value },
      {
        onSuccess: () => setInputs((s) => ({ ...s, [kind]: '' })),
        onError: (err: any) =>
          setError(err.response?.data?.detail || 'Could not save integration'),
      }
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Integrations</CardTitle>
        <CardDescription>
          Connect external tools so agents can act on them. Secrets are encrypted
          and never shown again.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {INTEGRATIONS.map((it) => {
          const info = byKind[it.kind];
          return (
            <div key={it.kind} className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor={`int-${it.kind}`}>{it.label}</Label>
                {info?.configured && (
                  <span className="text-xs text-muted-foreground">Connected</span>
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  id={`int-${it.kind}`}
                  type="password"
                  placeholder={
                    info?.configured ? 'Enter a new URL to replace' : it.placeholder
                  }
                  value={inputs[it.kind] ?? ''}
                  onChange={(e) =>
                    setInputs((s) => ({ ...s, [it.kind]: e.target.value }))
                  }
                  autoComplete="off"
                />
                <Button
                  onClick={() => save(it.kind)}
                  disabled={setIntegration.isPending || !(inputs[it.kind] || '').trim()}
                >
                  Save
                </Button>
                {info?.configured && (
                  <Button
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    disabled={removeIntegration.isPending}
                    onClick={() => removeIntegration.mutate(it.kind)}
                  >
                    Remove
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {it.hint} Enable the <code>{it.tool}</code> tool on an agent to use it.
              </p>
            </div>
          );
        })}

        <div className="space-y-1.5 border-t pt-4">
          <div className="flex items-center justify-between">
            <Label>Google Calendar</Label>
            {googleConnected ? (
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                disabled={removeIntegration.isPending}
                onClick={() => removeIntegration.mutate('google_calendar')}
              >
                Disconnect
              </Button>
            ) : (
              <Button size="sm" onClick={connectGoogle} disabled={connecting}>
                {connecting ? 'Connecting…' : 'Connect'}
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {googleConnected ? 'Connected. ' : ''}Let agents read and create events
            with the <code>google_calendar_list_events</code> /{' '}
            <code>google_calendar_create_event</code> tools.
          </p>
          {googleMsg && <p className="text-sm text-primary">{googleMsg}</p>}
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
      </CardContent>
    </Card>
  );
}

const PROVIDERS = [
  { id: 'openai', label: 'OpenAI', placeholder: 'sk-…' },
  { id: 'anthropic', label: 'Anthropic', placeholder: 'sk-ant-…' },
];

function ProviderKeysCard() {
  const { data: keys } = useLLMKeys();
  const setKey = useSetLLMKey();
  const removeKey = useDeleteLLMKey();
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const byProvider = Object.fromEntries((keys ?? []).map((k) => [k.provider, k]));

  function save(provider: string) {
    setError(null);
    const apiKey = (inputs[provider] || '').trim();
    if (!apiKey) return;
    setKey.mutate(
      { provider, apiKey },
      {
        onSuccess: () => setInputs((s) => ({ ...s, [provider]: '' })),
        onError: (err: any) =>
          setError(err.response?.data?.detail || 'Could not save key'),
      }
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>AI provider keys</CardTitle>
        <CardDescription>
          Bring your own LLM keys — your agents use them for runs instead of the
          shared default. Keys are encrypted and never shown again.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {PROVIDERS.map((p) => {
          const info = byProvider[p.id];
          return (
            <div key={p.id} className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor={`key-${p.id}`}>{p.label}</Label>
                {info?.configured && (
                  <span className="text-xs text-muted-foreground">
                    Set · ••••{info.last4}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  id={`key-${p.id}`}
                  type="password"
                  placeholder={info?.configured ? 'Enter a new key to replace' : p.placeholder}
                  value={inputs[p.id] ?? ''}
                  onChange={(e) =>
                    setInputs((s) => ({ ...s, [p.id]: e.target.value }))
                  }
                  autoComplete="off"
                />
                <Button
                  onClick={() => save(p.id)}
                  disabled={setKey.isPending || !(inputs[p.id] || '').trim()}
                >
                  Save
                </Button>
                {info?.configured && (
                  <Button
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    disabled={removeKey.isPending}
                    onClick={() => removeKey.mutate(p.id)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
          );
        })}
        {error && <p className="text-sm text-red-600">{error}</p>}
      </CardContent>
    </Card>
  );
}
